/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { Button, Text, useMantineTheme, rem, Group, type MantineStyleProps } from '@mantine/core';
import { NavLink } from 'react-router-dom';

import { navLink } from './nav-link.css';
import { TextVariants } from '../typography/variants';

type NavigationTabProps = {
  label: string;
  path: string;
  disabled?: boolean;
  onClick?: (path: string) => void;
};

export const NavigationTabs = ({
  values,
  renderTab,
  h = rem(48),
  px,
  preventScrollReset,
}: {
  values: NavigationTabProps[];
  renderTab?: ({ label, disabled, onClick }: NavigationTabProps) => JSX.Element;
  h?: MantineStyleProps['h'];
  px?: MantineStyleProps['px'];
  preventScrollReset?: boolean;
}) => {
  const theme = useMantineTheme();

  return (
    <Group gap={theme.pf.layout.lg} h={h} px={px} wrap="nowrap" style={{ overflowX: 'auto' }}>
      {values.map(({ label, disabled, path, onClick }, index) => {
        const key = `${path}-${index}`;

        return disabled ? (
          <Button variant="transparent" key={key} px={0} disabled>
            {renderTab ? renderTab({ label, disabled, path }) : label}
          </Button>
        ) : (
          <NavLink
            key={key}
            className={navLink}
            style={({ isActive }) => ({
              paddingTop: isActive ? rem(3) : 0,
              color: isActive ? theme.pf.color.fg.neutral.strong : undefined,
              borderBottom: isActive
                ? `${rem(3)} solid ${theme.pf.color.border.neutral.strong}`
                : undefined,
            })}
            to={path}
            onClick={onClick ? () => onClick(path) : undefined}
            preventScrollReset={preventScrollReset}
          >
            {renderTab ? (
              renderTab({ label, disabled, path })
            ) : (
              <Text style={{ whiteSpace: 'nowrap' }} variant={TextVariants.TitleSm}>
                {label}
              </Text>
            )}
          </NavLink>
        );
      })}
    </Group>
  );
};
